import { addSeconds, differenceInSeconds, format } from 'date-fns';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * useMediaRecorder
 *
 * Provides mediaRecorder api as a hook
 */
export const useMediaRecorder = () => {
  const medRef = useRef<MediaRecorder>();
  const [_interval, _setInterval] = useState<NodeJS.Timeout>();
  const [fileUrl, setFileUrl] = useState<string>();
  const [blob, setBlob] = useState<Blob>();
  const [timeRecorded, setTimeRecorded] = useState('00:00');
  const [isRecording, setIsRecording] = useState(false);

  /**
   * clear interval on unmount
   */
  useEffect(
    () => () => {
      if (_interval != null) clearInterval(_interval);
    },
    [],
  );

  const startRecording = useCallback(
    () =>
      new Promise((res, rej) => {
        navigator.mediaDevices
          .getUserMedia({
            audio: true,
          })
          .then((s) => {
            const med = new MediaRecorder(s);
            medRef.current = med;

            const start = new Date();

            /**
             * Update recording timer every second
             */
            const interval = setInterval(() => {
              const now = new Date();
              const sec = differenceInSeconds(now, start);
              setTimeRecorded(format(addSeconds(new Date(0), sec), 'mm:ss'));
            }, 1000);

            /**
             * casting to any so that jest doesn't get in a huff
             * TODO: fix ts-jest config so that this isn't a problem
             */
            _setInterval(interval as any);

            med.onstop = () => {
              med?.stream.getTracks().forEach((t) => {
                t.stop();
              });

              /**
               * If we don't clear the interval here the next
               * interval will try to update the state when the component is unmounted
               * leading to an exception
               */
              if (_interval != null) {
                clearInterval(_interval);
              }
            };

            med.start();
            setIsRecording(true);

            /**
             * casting to any so that jest doesn't get in a huff
             * TODO: fix ts-jest config so that this isn't a problem
             */
            _setInterval(interval as any);
            res({});
          })
          .catch((err) => {
            rej(err);
          });
      }),
    [_interval],
  );

  const stopRecording = useCallback(
    () =>
      new Promise<Blob>((res) => {
        if (blob != null) {
          res(blob);
        } else if (medRef.current != null) {
          medRef.current.addEventListener('stop', () => {
            medRef.current = undefined;
          });

          medRef.current.addEventListener('dataavailable', (e) => {
            const _blob = new Blob([e.data], {
              type: 'audio/wav',
            });
            setFileUrl(window.URL.createObjectURL(_blob));
            setBlob(e.data);
            res(e.data);
          });

          medRef.current.stop();
          setIsRecording(false);
        } else {
          // purely there to satisfy type constraint
          res(new Blob());
        }
      }),
    [],
  );

  return {
    startRecording,
    blob,
    timeRecorded,
    fileUrl,
    isRecording,
    stopRecording,
  };
};
